import { TA } from './tourAnchors';
import client from '../../api/client';

// Each script.steps[i].gate determines how that step advances:
//   { type: 'manual' }                                    — user taps Next
//   { type: 'click' }                                     — auto-advance on click of anchor
//   { type: 'custom', event, condition: (payload) => ... } — advance when bus event matches predicate

export const TOURS = {
  // First-run spotlight tour of the primary navigation. The anchors live in the
  // mobile bottom nav (md:hidden), so on desktop the steps find no target and
  // TourOverlay bails out gracefully — acceptable, clients are mobile-first.
  // Runs AFTER WelcomeTutorialModal (which owns the
  // 'welcome' id and renders at z-85, above this overlay's z-80) — the modal's
  // "Got it" flips this tour to pending, so the two can never collide.
  welcome_nav: {
    id: 'welcome_nav',
    triggerPath: '/dashboard',
    steps: [
      { anchor: TA.NAV_WORKOUT, copyKey: 'tour.welcome.workout', gate: { type: 'manual' }, showSkip: true },
      { anchor: TA.NAV_DIET,    copyKey: 'tour.welcome.diet',    gate: { type: 'manual' }, showSkip: true },
      { anchor: TA.NAV_SOCIAL,  copyKey: 'tour.welcome.social',  gate: { type: 'manual' } },
      { anchor: null,           copyKey: 'tour.welcome.done',    gate: { type: 'manual' }, isEndCard: true, onAdvance: 'complete' },
    ],
  },
  workouts: {
    id: 'workouts',
    triggerPath: '/workout',
    steps: [
      { anchor: TA.ROUTINES_HEADER,    copyKey: 'tour.workouts.intro',       gate: { type: 'manual' }, showSkip: true },
      { anchor: TA.ROUTINES_CREATE_BTN, copyKey: 'tour.workouts.tapCreate',  gate: { type: 'click' } },
      { anchor: TA.WIZARD_SHEET,       copyKey: 'tour.workouts.wizardIntro', gate: { type: 'custom', event: 'wizard.step', condition: (p) => p?.step === 'preview' } },
      { anchor: TA.WIZARD_SAVE_BTN,    copyKey: 'tour.workouts.savePrompt',  gate: { type: 'click' }, onAdvance: 'complete' },
    ],
  },
  live_workout: {
    id: 'live_workout',
    triggerPath: '/workout/', // prefix match — any /workout/:id
    steps: [
      { anchor: TA.WORKOUT_FIRST_SET_ROW, copyKey: 'tour.live_workout.firstSet', gate: { type: 'custom', event: 'workout.first-set-completed', condition: (p) => p === true }, showSkip: true },
      { anchor: TA.WORKOUT_REST_TIMER,    copyKey: 'tour.live_workout.restTimer', gate: { type: 'manual' } },
      { anchor: TA.WORKOUT_FINISH_BTN,    copyKey: 'tour.live_workout.finishBtn', gate: { type: 'manual' } },
      {
        anchor: null,
        copyKey: 'tour.live_workout.endCard',
        gate: { type: 'manual' },
        isEndCard: true,
        onAdvance: 'complete',
        endCardActions: [
          {
            id: 'continue',
            labelKey: 'tour.live_workout.continueTraining',
            handler: ({ advance }) => advance(),
            className: 'bg-orange-500 hover:bg-orange-600 text-white',
          },
          {
            id: 'discard',
            labelKey: 'tour.live_workout.discardExit',
            handler: async ({ advance }) => {
              // Delete the workout session (best-effort; swallow 404 for unsaved sessions)
              try {
                const sessionId = window.location.pathname.split('/workout/')[1];
                if (sessionId) {
                  await client.delete(`/workout-sessions/${sessionId}`);
                }
              } catch { /* swallow per spec */ }
              // Persist tour completion BEFORE hard navigation so the PATCH isn't aborted.
              // advance() triggers React state updates that enqueue an async PATCH — not
              // safe to rely on before window.location.href replaces the page context.
              try {
                await client.patch('/users/me/tutorial-progress', { tour_id: 'live_workout', state: 'completed' });
              } catch { /* swallow */ }
              advance(); // also updates in-memory React state for consistency
              window.location.href = '/dashboard';
            },
            className: 'bg-zinc-700 hover:bg-zinc-600 text-white',
          },
        ],
      },
    ],
  },
  diet: {
    id: 'diet',
    triggerPath: '/diet',
    steps: [
      { anchor: TA.DIET_ADD_FOOD_BTN,  copyKey: 'tour.diet.tapAdd',     gate: { type: 'click' }, showSkip: true },
      { anchor: TA.DIET_PICKER_SEARCH, copyKey: 'tour.diet.searchFood', gate: { type: 'custom', event: 'diet.food-added', condition: () => true } },
      { anchor: null, copyKey: 'tour.diet.done', gate: { type: 'manual' }, isEndCard: true, onAdvance: 'complete' },
    ],
  },
  check_ins: {
    id: 'check_ins',
    triggerPath: '/measurements', // confirmed in App.js — path="/measurements" → MeasurementsPage
    steps: [
      { anchor: TA.CHECKINS_NEW_BTN,   copyKey: 'tour.check_ins.newBtn',    gate: { type: 'manual' }, showSkip: true },
      { anchor: TA.CHECKINS_INFO_AREA, copyKey: 'tour.check_ins.autoFeeds', gate: { type: 'manual' }, onAdvance: 'complete' },
    ],
  },
};

// Match a pathname against a tour's triggerPath. Returns the tour or null.
export function getTourForPath(pathname) {
  for (const t of Object.values(TOURS)) {
    if (pathname === t.triggerPath) return t;
    // Prefix match for /workout/:id (the live_workout tour uses '/workout/' as the prefix)
    if (t.triggerPath === '/workout/' && pathname.startsWith('/workout/')) return t;
  }
  return null;
}
